import styled, { createGlobalStyle, css, keyframes } from 'styled-components'

/**
 * The answer is what the page is about, so the conversation stays quiet: one
 * column at a readable measure, Miranda's text and surface tokens. The one loud
 * thing is the composer's ring, in the brand's two signal colours. It lights
 * up when the composer has focus and turns while an answer is being written,
 * so the state of the run reads from where the user's eyes already are.
 */

/** Registered so the ring's angle can be animated; a plain custom property only jumps. */
export const RingAngleProperty = createGlobalStyle`
  @property --assistant-ring-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }
`

const turn = keyframes`
  to { --assistant-ring-angle: 360deg; }
`

export const StyledChat = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-3);
  width: 100%;
  max-width: 760px;
  /* dvh follows the visible viewport on phones, where vh ignores the browser bars. */
  height: calc(100vh - 128px);
  height: calc(100dvh - 128px);
  min-height: 420px;
  margin: 0 auto;

  /* ------------------------------------------------------------ messages */

  .ai-chat-conversation-content {
    gap: var(--m-spacing-6);
  }

  /* The question is the user's own voice: the brand's deep teal, set apart from the answer. */
  .ai-chat-message[data-message-role='user'] .ai-chat-markdown {
    /* The library sets the text colour inline on this element. */
    --m-text-color: rgb(var(--m-color-neutral-0)) !important;

    padding: 10px var(--m-spacing-4);
    border-radius: 18px 18px 4px 18px;
    background-color: rgb(var(--m-color-primary-100));
  }

  .ai-chat-message[data-message-role='user'] .ai-chat-markdown p {
    line-height: 1.5;
  }

  .ai-chat-message[data-message-role='assistant'] .ai-chat-markdown {
    display: flex;
    flex-direction: column;
    gap: var(--m-spacing-3);
  }

  .ai-chat-message[data-message-role='assistant'] .ai-chat-markdown p,
  .ai-chat-message[data-message-role='assistant'] .ai-chat-markdown li {
    font-size: 0.9375rem;
    line-height: 1.65;
  }

  .ai-chat-markdown strong {
    font-weight: var(--m-font-weight-bold);
    color: rgb(var(--m-color-primary-100));
  }

  /* --------------------------------------------------------------- tables */

  /* Answers are mostly figures, so a table gets a frame, a header band and digits that line up. */
  .ai-chat-markdown table {
    display: block;
    width: max-content;
    max-width: 100%;
    overflow-x: auto;
    border: var(--m-border-thin) solid rgb(var(--m-color-border));
    border-radius: 12px;
    border-spacing: 0;
    font-size: var(--m-font-size-3);
    font-variant-numeric: tabular-nums;
  }

  .ai-chat-markdown th,
  .ai-chat-markdown td {
    padding: var(--m-spacing-2) var(--m-spacing-4);
    border-bottom: var(--m-border-thin) solid rgb(var(--m-color-border));
    text-align: left;
    white-space: nowrap;
  }

  .ai-chat-markdown th {
    background-color: rgb(var(--m-color-background-secondary));
    font-size: var(--m-font-size-2);
    font-weight: var(--m-font-weight-medium);
    color: rgb(var(--m-color-text-tertiary));
  }

  .ai-chat-markdown tbody tr:last-child td {
    border-bottom: 0;
  }

  .ai-chat-markdown tbody tr:hover td {
    background-color: rgb(var(--m-color-background-secondary));
  }
`

export const StyledConversation = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
`

/* ----------------------------------------------------------- empty state */

export const StyledStarters = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: var(--m-spacing-6);
  padding: var(--m-spacing-6) 0;
`

export const StyledIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--m-spacing-2);
`

export const StyledHeadline = styled.h1`
  margin: 0;
  max-width: 18ch;
  font-family: var(--m-font-family-title);
  font-size: clamp(1.75rem, 1.2rem + 1.6vw, 2.375rem);
  font-weight: var(--m-font-weight-bold);
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: rgb(var(--m-color-primary-100));
`

export const StyledLede = styled.p`
  margin: 0;
  max-width: 52ch;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-4);
  line-height: 1.5;
  color: rgb(var(--m-color-text-tertiary));
`

/** A list, not cards: each question is one line the user can read at a glance and send with a click. */
export const StyledStarterList = styled.ul`
  margin: 0;
  padding: 0;
  border-top: var(--m-border-thin) solid rgb(var(--m-color-border));
  list-style: none;

  li {
    border-bottom: var(--m-border-thin) solid rgb(var(--m-color-border));
  }
`

export const StyledStarter = styled.button`
  display: flex;
  align-items: center;
  gap: var(--m-spacing-3);
  width: 100%;
  padding: 14px var(--m-spacing-2);
  border: 0;
  border-radius: var(--m-border-radius-m);
  background: none;
  font-family: var(--m-font-family-default);
  font-size: 0.9375rem;
  line-height: var(--m-line-height-5);
  text-align: left;
  color: rgb(var(--m-color-text-primary));
  cursor: pointer;
  transition:
    background-color 120ms ease,
    padding-left 160ms ease;

  /* The icon names the subject of the question: warehouses, carriers, timing, cancellations. */
  m-icon {
    --m-icon-color: rgb(var(--m-color-text-placeholder));

    flex: none;
  }

  &:hover {
    padding-left: var(--m-spacing-3);
    background-color: rgb(var(--m-color-background-secondary));
  }

  &:hover m-icon {
    --m-icon-color: rgb(var(--m-color-primary-100));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: -2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/* -------------------------------------------------------------- composer */

export const StyledComposerBox = styled.div`
  position: relative;
`

export const StyledComposer = styled.div<{ $isWorking: boolean }>`
  padding-bottom: var(--m-spacing-3);

  .ai-chat-composer-form {
    border-radius: 20px;
    background-color: rgb(var(--m-color-background-primary));
    box-shadow:
      0 1px 2px rgba(var(--m-color-primary-100), 0.06),
      0 16px 40px -24px rgba(var(--m-color-primary-100), 0.35);
  }

  /* The ring is drawn here, so the text area's own border steps aside. */
  .ai-chat-composer-input {
    --m-text-area-border-color: transparent;
    --m-text-area-background-color: transparent;

    border-radius: 20px;
  }

  .ai-chat-composer-form::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    padding: 1.5px;
    border-radius: inherit;
    background: rgb(var(--m-color-border-input));
    pointer-events: none;
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }

  .ai-chat-composer-form:focus-within::before {
    background: linear-gradient(
      100deg,
      rgb(var(--m-color-brand-accent-1)),
      rgb(var(--m-color-brand-accent-2))
    );
  }

  ${({ $isWorking }) =>
    $isWorking &&
    css`
      .ai-chat-composer-form::before {
        padding: 2px;
        background: conic-gradient(
          from var(--assistant-ring-angle),
          rgb(var(--m-color-brand-accent-1)),
          rgb(var(--m-color-brand-accent-2)),
          rgba(var(--m-color-brand-accent-2), 0.15) 40%,
          rgba(var(--m-color-brand-accent-1), 0.15) 70%,
          rgb(var(--m-color-brand-accent-1))
        );
        animation: ${turn} 2.4s linear infinite;
      }

      @media (prefers-reduced-motion: reduce) {
        .ai-chat-composer-form::before {
          animation: none;
          background: linear-gradient(
            100deg,
            rgb(var(--m-color-brand-accent-1)),
            rgb(var(--m-color-brand-accent-2))
          );
        }
      }
    `}

  .ai-chat-composer-send {
    z-index: 2;
  }
`

/** Stop sits inside the composer, where Send was, while an answer is written. */
export const StyledStop = styled.button`
  position: absolute;
  bottom: 14px;
  left: var(--m-spacing-4);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: var(--m-spacing-2);
  padding: 6px var(--m-spacing-3) 6px 10px;
  border: var(--m-border-thin) solid rgb(var(--m-color-border));
  border-radius: 999px;
  background-color: rgb(var(--m-color-background-primary));
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  font-weight: var(--m-font-weight-bold);
  color: rgb(var(--m-color-primary-100));
  cursor: pointer;

  /* A filled square: the stop sign every player uses. */
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background-color: currentColor;
  }

  &:hover:not(:disabled) {
    background-color: rgb(var(--m-color-background-secondary));
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--m-color-brand));
    outline-offset: 2px;
  }

  /* On touch the button is a real target. */
  @media (hover: none) {
    min-height: 44px;
  }
`

export const StyledHint = styled.p`
  margin: var(--m-spacing-2) 0 0;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  line-height: var(--m-line-height-5);
  text-align: center;
  color: rgb(var(--m-color-text-placeholder));
`

export const StyledNotice = styled.p`
  margin: var(--m-spacing-2) 0 0;
  font-family: var(--m-font-family-default);
  font-size: var(--m-font-size-2);
  line-height: var(--m-line-height-5);
  text-align: center;
  color: rgb(var(--m-color-text-tertiary));
`
